<template>
    <el-input ref="campo"
              :value="texto"
              :disabled="disabled"
              size="mini"
              @keydown.native="onKeydown"
              @paste.native="onPaste"
              @blur="onBlur">
    </el-input>
</template>

<script>
/**
 * Campo de nota com máscara de centavos: o usuário digita só algarismos e a
 * vírgula anda sozinha da direita para a esquerda.
 *
 *   1      -> 0,01
 *   1 1    -> 0,11
 *   1 1 1  -> 1,11
 *   1 1 1 1 -> recusado, porque 11,11 passa do limite
 *
 * O limite é o menor valor entre 10 (teto do período) e a sobra do aluno naquele
 * período, informada pelo pai em nota-maxima. Nada é aplicado quando o limite é
 * estourado: o componente avisa e mantém o valor anterior.
 */
export default {
    name: 'nota-input',

    props: {

        value: {
            required: false,
            default: ''
        },

        //SOBRA DO ALUNO NAQUELE PERÍODO. null SIGNIFICA "SÓ O TETO DE 10"
        notaMaxima: {
            required: false,
            default: null
        },

        disabled: {
            required: false,
            type: Boolean,
            default: false
        },

        teto: {
            required: false,
            type: Number,
            default: 10
        }
    },

    data(){

        return {
            //FONTE DA VERDADE ENQUANTO SE DIGITA: A SEQUÊNCIA DE ALGARISMOS EM CENTAVOS.
            //GUARDADA SEPARADA DO MODELO PARA DISTINGUIR CAMPO VAZIO DE NOTA ZERO.
            digitos: '',
            //CAMPO FOI LIMPO POR ESTOURO DE LIMITE, NÃO PELO USUÁRIO
            limiteEstourado: false
        }
    },

    computed: {

        texto(){

            if(this.digitos === '')
                return '';

            return (parseInt(this.digitos, 10) / 100).toFixed(2).replace('.', ',');
        },

        limite(){

            if(this.notaMaxima === null || this.notaMaxima === undefined || this.notaMaxima === '')
                return this.teto;

            let n = Number(this.notaMaxima);

            if(isNaN(n))
                return this.teto;

            return Math.min(this.teto, n);
        }
    },

    watch: {

        value(novo){

            this.sincroniza(novo);
        }
    },

    mounted(){

        this.sincroniza(this.value);
    },

    methods: {

        /**
         * O valor só chega ao modelo aqui, ao sair do campo. Emitir a cada tecla fazia
         * os valores intermediários da máscara (0,01 -> 0,10 -> 1,00) virarem candidatos
         * a gravação: bastava a digitação seguinte estourar o limite para o penúltimo
         * ser salvo sem o usuário ter pedido.
         */
        onBlur(){

            //SAIU SEM DIGITAR DEPOIS DO AVISO: DEVOLVE O VALOR QUE ESTAVA, SEM GRAVAR
            if(this.limiteEstourado && this.digitos === ''){

                this.limiteEstourado = false;
                this.sincroniza(this.value);
                this.$emit('blur');
                return;
            }

            this.limiteEstourado = false;

            let novo = this.digitos === '' ? '' : parseInt(this.digitos, 10) / 100;
            let atual = this.converte(this.value);

            if(novo !== atual)
                this.$emit('input', novo);

            this.$emit('blur');
        },

        converte(v){

            if(v === '' || v === null || v === undefined)
                return '';

            let n = parseFloat(String(v).replace(',', '.'));

            return isNaN(n) ? '' : n;
        },

        sincroniza(v){

            if(v === '' || v === null || typeof v === 'undefined'){

                this.digitos = '';
                return;
            }

            let n = parseFloat(String(v).replace(',', '.'));

            if(isNaN(n)){

                this.digitos = '';
                return;
            }

            this.digitos = String(Math.round(n * 100));
        },

        onKeydown(e){

            if(this.disabled)
                return;

            //DEIXA PASSAR NAVEGAÇÃO E ATALHOS (COPIAR, COLAR, SELECIONAR TUDO)
            if(e.ctrlKey || e.metaKey || e.altKey)
                return;

            if(['Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].indexOf(e.key) !== -1)
                return;

            e.preventDefault();

            //QUALQUER TECLA DEPOIS DO AVISO SIGNIFICA QUE O USUÁRIO ESTÁ REDIGITANDO
            this.limiteEstourado = false;

            if(e.key === 'Backspace' || e.key === 'Delete'){

                this.aplica(this.digitos.slice(0, -1));
                return;
            }

            if(!/^[0-9]$/.test(e.key))
                return;

            //DIGITAR COM O CAMPO SELECIONADO SUBSTITUI, NÃO CONCATENA
            this.aplica(this.selecionouTudo() ? e.key : this.digitos + e.key);
        },

        onPaste(e){

            if(this.disabled)
                return;

            e.preventDefault();

            let texto = (e.clipboardData || window.clipboardData).getData('text') || '';
            let limpo = texto.replace(/\D/g, '');

            if(limpo !== '')
                this.aplica(limpo);
        },

        selecionouTudo(){

            let campo = this.$refs.campo ? this.$refs.campo.$refs.input : null;

            if(!campo)
                return false;

            return campo.selectionStart !== campo.selectionEnd
                    && campo.selectionEnd - campo.selectionStart >= String(campo.value).length;
        },

        aplica(novosDigitos){

            //TIRA ZEROS À ESQUERDA PARA O CAMPO NÃO CRESCER SEM FIM
            let limpos = novosDigitos.replace(/^0+(?=\d)/, '');

            if(limpos === ''){

                this.digitos = '';
                return;
            }

            let valor = parseInt(limpos, 10) / 100;

            if(valor > this.limite){

                //LIMPA O CAMPO PARA O USUÁRIO DIGITAR DE NOVO. O VALOR GRAVADO NÃO MUDA:
                //SE ELE SAIR SEM DIGITAR, O onBlur DEVOLVE O QUE ESTAVA.
                this.digitos = '';
                this.limiteEstourado = true;

                this.$emit('alert-maximo', this.limite);
                return;
            }

            this.limiteEstourado = false;
            this.digitos = limpos;
        }
    }
}
</script>
